import { useEffect, type ReactElement } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AppErrorFallback } from '@/components/AppErrorFallback'

/**
 * Route-level error boundary, attached as `errorElement` on the root route.
 *
 * This exists because react-router catches render errors thrown inside a route
 * *before* any React error boundary above `RouterProvider` can see them: with
 * only the top-level Sentry.ErrorBoundary, a crashing page rendered
 * react-router's own error screen and nothing was ever reported. The boundary in
 * `main.tsx` still covers everything outside the router (the providers, or the
 * router itself failing to start).
 */
export function RouteErrorBoundary(): ReactElement {
  const error = useRouteError()

  // A thrown Response (a loader's 404/redirect) is routing control flow, not a
  // defect, so it is not reported. Nothing throws Responses today; this keeps
  // the boundary honest if a loader ever does.
  const isResponse = isRouteErrorResponse(error)

  useEffect(() => {
    if (!isResponse) {
      Sentry.captureException(error)
    }
  }, [error, isResponse])

  return <AppErrorFallback />
}
